import React, { useMemo, useState } from "react";
import { Box, Button, ButtonGroup, HStack, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { BarList, ChartCard, ShareBar, StatTile, StatusIcon } from "./parts";
import SpendOverTime from "./SpendOverTime";
import { FAMILIES, familyColor, useVizTokens } from "./tokens";
import { cents, count, directionVerb, groupSeries, insights, money, percent } from "./format";

// The first tab: the bill, what to fix first, the headline numbers, spend over time and why
// texts go out.

const GRANULARITIES = [["day", "Days"], ["week", "Weeks"], ["month", "Months"]];

export function sourceRows(sources) {
  return (sources || []).map((s) => ({
    key: s.key,
    label: s.label,
    value: s.cost,
    valueLabel: money(s.cost),
    sub: `${count(s.texts)} texts · ${s.avg_segments} segments each`,
    tip: `${s.label}: ${money(s.cost)} for ${count(s.texts)} texts (${count(s.segments)} segments)`
      + (s.guessed ? `. ${count(s.guessed)} of them sent before tracking, so sorted by their wording.` : "."),
  }));
}

export default function OverviewPanel({ report, onShowTab }) {
  const t = useVizTokens();
  const [granularity, setGranularity] = useState(report.range.days > 120 ? "week" : "day");
  const totals = report.totals;
  const billed = report.billed;
  const rows = useMemo(() => groupSeries(report.series, granularity), [report.series, granularity]);
  const familyTotals = useMemo(() => {
    const out = {};
    rows.forEach((r) => FAMILIES.forEach((f) => { out[f.key] = (out[f.key] || 0) + r[f.key]; }));
    return out;
  }, [rows]);
  const findings = useMemo(() => insights(report), [report]);
  // The bill's lines are its own categories: slots in order, "Everything else" gray.
  const billSegments = billed.lines.map((line, i) => ({
    key: line.key, label: line.label, value: line.cost, color: line.key === "other" ? t.other : t.series[i] || t.other,
  }));
  const spike = report.spike || {};
  // The bill is the whole Twilio account's, other servers' texts included: the hero is this
  // database's texting unless every text is counted.
  const everyText = Boolean(report.include_outside);
  const outside = report.tracking?.outside || { texts: 0, cost: 0 };
  const hero = everyText ? billed.total : totals.cost;
  const verb = directionVerb(report);
  const way = (key) => (report.directions || []).find((d) => d.key === key) || { texts: 0, cost: 0 };

  return (
    <Stack spacing={4}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Box bg={t.surface} borderWidth="1px" borderColor={t.border} borderRadius="lg" p={5}>
          <Text fontSize="sm" color={t.textSecondary}>
            {everyText ? "Twilio bill for these days" : "Texting this database has a record of"}
          </Text>
          <Text fontSize="5xl" fontWeight="semibold" color={t.text} lineHeight="1.1" data-testid="sms-hero">
            {money(hero)}
          </Text>
          <Text fontSize="sm" color={t.textSecondary} mt={2}>
            {everyText ? `Texting itself was ${money(totals.cost)}: ` : ""}{count(totals.texts)} texts {verb} as{" "}
            {count(totals.segments)} segments, {totals.avg_segments} each. Twilio bills every segment
            {report.direction === "inbound" ? "." : ", and the carriers add a fee on each one sent."}
          </Text>
          <Text fontSize="sm" color={t.textSecondary} mt={2} data-testid="sms-directions">
            Outbound: <b>{money(way("outbound").cost)}</b> for {count(way("outbound").texts)} texts sent · Inbound:{" "}
            <b>{money(way("inbound").cost)}</b> for {count(way("inbound").texts)} texts received
          </Text>
          {!everyText && (
            <Text fontSize="sm" color={t.textSecondary} mt={2} data-testid="sms-account-bill">
              Twilio's bill for the whole account was {money(billed.total)}
              {outside.texts > 0
                ? `. It also covers ${count(outside.texts)} texts (${money(outside.cost)}) this database has no record of, the phone numbers and other fees.`
                : ", phone numbers and other fees included."}
            </Text>
          )}
          <Text fontSize="sm" color={t.text} mt={4}>
            At this rate: about <b>{money((hero / report.range.days) * 30)}</b> a month,{" "}
            <b>{money((hero / report.range.days) * 365)}</b> a year.
          </Text>
        </Box>
        <ChartCard title="Where the bill goes" subtitle="Twilio's own categories for the whole account (UTC days)"
                   table={{
                     columns: [
                       { key: "label", label: "Line" },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                       { key: "usage", label: "Amount", numeric: true,
                         render: (r) => (r.usage != null ? `${count(r.usage)} ${r.usage_unit || ""}` : "-") },
                     ],
                     rows: billed.lines,
                   }}>
          <ShareBar segments={billSegments} format={money} />
        </ChartCard>
      </SimpleGrid>

      {findings.length > 0 && (
        <Box bg={t.surface} borderWidth="1px" borderColor={t.border} borderRadius="lg" p={4}>
          <Text fontWeight="semibold" color={t.text} mb={3}>What to fix first</Text>
          <Stack spacing={3}>
            {findings.map((f) => (
              <HStack key={f.key} align="flex-start" spacing={3} data-testid={`finding-${f.key}`}>
                <Box pt="2px"><StatusIcon status={f.status} /></Box>
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" fontWeight="semibold" color={t.text}>{f.title}</Text>
                  {f.detail && <Text fontSize="sm" color={t.textSecondary}>{f.detail}</Text>}
                </Box>
                {onShowTab && f.tab !== "overview" && (
                  <Button size="xs" variant="ghost" onClick={() => onShowTab(f.tab)} flexShrink={0}>Show</Button>
                )}
              </HStack>
            ))}
          </Stack>
        </Box>
      )}

      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
        <StatTile label={`Texts ${verb}`} value={count(totals.texts)}
                  help={`${verb === "sent" ? `${count(totals.incoming_texts)} received · ` : ""}${count(totals.patients)} patients`} />
        <StatTile label="Segments per text" value={totals.avg_segments}
                  help="1 segment is up to 153 plain characters"
                  status={totals.avg_segments > totals.target_segments ? "warning" : "good"}
                  statusWords={totals.avg_segments > totals.target_segments ? "Long: aim for 1–2" : "Short"} />
        <StatTile label="Cost per text" value={cents(totals.cost_per_text)}
                  help={`${cents(totals.cost_per_segment)} a segment, carrier fee included`} />
        <StatTile label="Paid for texts that never arrived" value={money(totals.undelivered_cost)}
                  help={`${count(totals.undelivered_texts)} texts`}
                  status={totals.undelivered_cost > 0 ? "critical" : "good"}
                  statusWords={totals.undelivered_cost > 0 ? "Wasted" : "None"} />
        <StatTile label={`Texts over ${totals.target_segments} segments cost extra`} value={money(totals.savings_long)}
                  help={`what shortening them to ${totals.target_segments} segments would save`} />
        <StatTile label="Today so far" value={spike.today == null ? "-" : money(spike.today)}
                  help={spike.average != null ? `${money(spike.average)} a day the week before` : undefined}
                  status={spike.alert ? "critical" : undefined}
                  statusWords={spike.alert ? `Spike: ${spike.ratio ? `${spike.ratio}×` : "far above"} usual` : undefined} />
      </SimpleGrid>

      <ChartCard
        title="What texting cost over time"
        subtitle={`Stacked by why the texts went out · ${money(totals.cost)} in all`}
        actions={(
          <ButtonGroup size="xs" isAttached variant="outline">
            {GRANULARITIES.map(([key, label]) => (
              <Button key={key} onClick={() => setGranularity(key)} aria-pressed={granularity === key}
                      variant={granularity === key ? "solid" : "outline"}>
                {label}
              </Button>
            ))}
          </ButtonGroup>
        )}
        table={{
          columns: [
            { key: "label", label: granularity === "day" ? "Day" : granularity === "week" ? "Week" : "Month" },
            { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
            { key: "segments", label: "Segments", numeric: true, render: (r) => count(r.segments) },
            { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
            ...FAMILIES.map((f) => ({ key: f.key, label: f.label, numeric: true, render: (r) => money(r[f.key]) })),
          ],
          rows,
        }}
      >
        <SpendOverTime rows={rows} totals={familyTotals} />
      </ChartCard>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <ChartCard title="Why texts are sent"
                   subtitle="Cost by reason. Texts sent before tracking began are sorted by their wording."
                   table={{
                     columns: [
                       { key: "label", label: "Reason" },
                       { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
                       { key: "avg_segments", label: "Segments each", numeric: true },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                       { key: "guessed", label: "Guessed from wording", numeric: true, render: (r) => count(r.guessed) },
                     ],
                     rows: report.sources,
                   }}>
          <BarList rows={sourceRows(report.sources)} />
        </ChartCard>
        <ChartCard title="Started by staff or sent automatically"
                   subtitle="Texts sent before tracking began can't always tell"
                   table={{
                     columns: [
                       { key: "label", label: "Started" },
                       { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                     ],
                     rows: report.modes,
                   }}>
          <ShareBar format={money} segments={report.modes.map((m, i) => ({
            key: m.key, label: `${m.label} · ${count(m.texts)} texts`, value: m.cost,
            color: m.key === "unknown" ? t.other : m.key === "received" ? familyColor(t, "received") : t.series[i],
          }))} />
          {report.direction !== "inbound" && (
            <Text fontSize="xs" color={t.muted} mt={3}>
              {percent(report.modes.find((m) => m.key === "automatic")?.cost || 0, totals.cost)} of texting cost was sent
              without anyone pressing send.
            </Text>
          )}
        </ChartCard>
      </SimpleGrid>
    </Stack>
  );
}
